import anime from "animejs";
import * as rive from "@rive-app/canvas";
import { GameData, RevealHistoryEntry } from "./gameData";
import { CreateRandomForest, CreateRandomMine, ShuffleHiddenForest, ShuffleHiddenMine, Tile } from "./map";
import { Random } from "./random";
import { mountHeaderBrandLogos } from "../../Shared/brandLogos";

const MESSAGE_VISIBLE_MS = 3000;
const STARTING_AREA_LEVEL = 0;

export let rng = new Random(null);
export let urlPath = '';
export let gameData = new GameData();
export let mineData: Tile[][] = [];
export let forestData: Tile[][] = [];

let messageTimeoutId: number | undefined;
let revealHistory: RevealHistoryEntry[] = [];

document.addEventListener("DOMContentLoaded", () => {
    mountHeaderBrandLogos();
    setupAppMenu();
    setupTendLogo();
    adjustFullScreen();

    const extraPath = window.location.pathname;
    urlPath = extraPath.replace(/^\/+|\/+$/g, "");

    if (urlPath !== '') {
        rng = new Random(urlPath);
    }

    gameData.checkForSavedData().then((hasSavedData) => {
        if (!hasSavedData) {
            CreateNewMaps();
        }
    });
});

function setupTendLogo() {
    const logoCanvas = document.getElementById("tend-icon-riv");
    if (!(logoCanvas instanceof HTMLCanvasElement)) {
        return;
    }

    let logoRive: rive.Rive | undefined;
    logoRive = new rive.Rive({
        src: "https://ucarecdn.com/c0873536-c559-4232-ba1b-0f2e62745dc1/tend_logo_v4.riv",
        canvas: logoCanvas,
        autoplay: true,
        artboard: "Tend Icon Only",
        stateMachines: "Logo Reveal",
        onLoad: () => {
            logoRive?.resizeDrawingSurfaceToCanvas();
        },
    });
}

function setupAppMenu() {
    const menuCell = document.querySelector<HTMLElement>('.menu-cell');
    const menuButton = document.getElementById('menu-button') as HTMLButtonElement | null;
    const appMenu = document.getElementById('app-menu');
    const undoButton = document.getElementById('undo-button') as HTMLButtonElement | null;

    if (menuCell == null || menuButton == null || appMenu == null) {
        return;
    }

    const setMenuOpen = (isOpen: boolean) => {
        menuCell.classList.toggle('is-open', isOpen);
        menuButton.setAttribute('aria-expanded', isOpen.toString());
        menuButton.setAttribute('aria-label', isOpen ? 'Close menu' : 'Open menu');
        appMenu.setAttribute('aria-hidden', (!isOpen).toString());
    };

    menuButton.addEventListener('click', (event) => {
        event.stopPropagation();
        setMenuOpen(!menuCell.classList.contains('is-open'));
    });

    appMenu.addEventListener('click', (event) => {
        event.stopPropagation();
    });

    undoButton?.addEventListener('click', () => {
        undoLastReveal();
    });

    document.addEventListener('click', () => {
        setMenuOpen(false);
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            setMenuOpen(false);
        }
    });

    syncUndoButtonState();
}

export function LoadSavedMap() {
    forestData = gameData.forest;
    mineData = gameData.mine;
    revealHistory = [...gameData.revealHistory];
    SetupMaps();
    syncUndoButtonState();
}

export function CreateNewMaps() {
    revealHistory = [];
    mineData = CreateRandomMine();
    forestData = CreateRandomForest();
    gameData.saveData(mineData, forestData, revealHistory);
    SetupMaps();
    syncUndoButtonState();
}

function SetupMaps() {
    const mineTopContainer = document.getElementById('mine-top');
    const mineBottomContainer = document.getElementById('mine-bottom');
    const forestTopContainer = document.getElementById('forest-top');
    const forestBottomContainer = document.getElementById('forest-bottom');

    if (mineTopContainer != null && mineBottomContainer != null) {
        mineTopContainer.replaceChildren();
        mineBottomContainer.replaceChildren();
        createGrid(mineData, mineTopContainer, mineBottomContainer, true);
    }

    if (forestTopContainer != null && forestBottomContainer != null) {
        forestTopContainer.replaceChildren();
        forestBottomContainer.replaceChildren();
        createGrid(forestData, forestTopContainer, forestBottomContainer, false);
    }

    syncLevelLabels();
}

function getNeighboringTilesNew(tile: Tile, tileSet: Tile[][]): Tile[] {
    const { x, y } = tile.position;
    const neighbors: Tile[] = [];

    if (y > 0) {
        neighbors.push(tileSet[y - 1][x]);
    }

    if (x < tileSet[0].length - 1) {
        neighbors.push(tileSet[y][x + 1]);
    }

    if (y < tileSet.length - 1) {
        neighbors.push(tileSet[y + 1][x]);
    }

    if (x > 0) {
        neighbors.push(tileSet[y][x - 1]);
    }

    return neighbors;
}

function DiscoverTile(tile: Tile, tileButton: HTMLButtonElement, saveGame: boolean, mines: boolean) {
    const wasDiscovered = tile.discovered;
    tile.discovered = true;

    let revealContent = tileButton.querySelector('.tile-reveal-content') as HTMLDivElement | null;
    if (revealContent == null) {
        revealContent = document.createElement('div');
        revealContent.classList.add('tile-reveal-content');
        tileButton.appendChild(revealContent);
    }

    revealContent.style.backgroundImage = "url(" + tile.type.img + ")";

    if (tile.upgrade) {
        if (mines) {
            revealContent.classList.add('grid-button-upgrade-mine');
        } else {
            revealContent.classList.add('grid-button-upgrade-forest');
        }

        if (revealContent.querySelector('.svg-upgrade') == null) {
            const svgDiv = document.createElement('div');
            svgDiv.classList.add('svg-upgrade');

            const imgElement = document.createElement('img');
            imgElement.src = 'https://ucarecdn.com/13f75182-48bb-48ec-b3b6-bfd6ddc3ad01/';
            imgElement.alt = 'Upgrade Icon';
            svgDiv.appendChild(imgElement);
            revealContent.appendChild(svgDiv);
        }
    } else {
        revealContent.classList.add('grid-button-no-upgrade');
    }

    setTileAccessibility(tileButton, tile, mines);

    if (saveGame && !wasDiscovered) {
        revealHistory.push({
            area: mines ? 'mine' : 'forest',
            x: tile.position.x,
            y: tile.position.y,
        });
    }

    if (saveGame) {
        gameData.saveData(mineData, forestData, revealHistory);
        syncUndoButtonState();
    }

    syncLevelLabels();

    if (!wasDiscovered) {
        anime({
            targets: revealContent,
            scale: [0, 1],
            duration: 600,
            easing: 'easeOutElastic(1, .5)'
        });
    }
}

function undoLastReveal() {
    const tile = popLastDiscoveredTile();

    if (tile == null) {
        gameData.saveData(mineData, forestData, revealHistory);
        syncUndoButtonState();
        return;
    }

    tile.discovered = false;
    ShuffleHiddenMine(mineData);
    ShuffleHiddenForest(forestData);
    gameData.saveData(mineData, forestData, revealHistory);
    SetupMaps();
    syncUndoButtonState();
}

function popLastDiscoveredTile(): Tile | null {
    while (revealHistory.length > 0) {
        const entry = revealHistory.pop();
        if (entry == null) {
            return null;
        }

        const tileSet = entry.area === 'mine' ? mineData : forestData;
        const tile = tileSet[entry.y]?.[entry.x];

        if (tile?.discovered === true) {
            return tile;
        }
    }

    return null;
}

function syncUndoButtonState() {
    const undoButton = document.getElementById('undo-button') as HTMLButtonElement | null;
    if (undoButton == null) {
        return;
    }

    undoButton.disabled = revealHistory.length === 0;
}

function syncLevelLabels() {
    syncLevelLabel('woods-level', forestData);
    syncLevelLabel('mine-level', mineData);
}

function syncLevelLabel(elementId: string, tileSet: Tile[][]) {
    const label = document.getElementById(elementId);
    if (label == null) {
        return;
    }

    const revealedUpgrades = tileSet
        .flat()
        .filter(tile => tile.discovered && tile.upgrade).length;
    label.textContent = `LVL ${STARTING_AREA_LEVEL + revealedUpgrades}`;
}

function createGrid(tiles: Tile[][], topContainer: HTMLElement, bottomContainer: HTMLElement, mines: boolean) {
    tiles.forEach((row, rowIndex) => {
        const rowContainer = document.createElement('div');
        rowContainer.classList.add('grid-row');

        row.forEach((tile) => {
            const tileButton = document.createElement('button');
            tileButton.type = 'button';
            tileButton.classList.add('grid-button');
            tileButton.dataset.x = tile.position.x.toString();
            tileButton.dataset.y = tile.position.y.toString();
            setTileAccessibility(tileButton, tile, mines);

            rowContainer.appendChild(tileButton);
            if (tile.discovered) {
                DiscoverTile(tile, tileButton, false, mines);
            }

            tileButton.addEventListener('click', () => {
                if (tile.discovered) {
                    return;
                }

                if (rowIndex === 0) {
                    DiscoverTile(tile, tileButton, true, mines);
                    return;
                }

                const neighbors = getNeighboringTilesNew(tile, tiles);
                const hasDiscoveredNeighbor = neighbors.some(neighbor => neighbor.discovered);

                if (hasDiscoveredNeighbor) {
                    DiscoverTile(tile, tileButton, true, mines);
                } else {
                    displayMessage('Choose a space in the top row or a space that is adjacent to an already discovered space.');
                }
            });
        });

        if (rowIndex < 4) {
            topContainer.appendChild(rowContainer);
        } else {
            bottomContainer.appendChild(rowContainer);
        }
    });
}

function setTileAccessibility(tileButton: HTMLElement, tile: Tile, mines: boolean) {
    const areaName = mines ? 'mine' : 'woods';
    const tileState = tile.discovered
        ? `${tile.type.name}${tile.upgrade ? ', upgrade' : ''}`
        : 'hidden';

    tileButton.setAttribute(
        'aria-label',
        `${areaName} row ${tile.position.y + 1}, column ${tile.position.x + 1}: ${tileState}`
    );
    tileButton.setAttribute('aria-pressed', String(tile.discovered));
}

function displayMessage(text: string) {
    const messageContainer = document.getElementById('message-container');
    if (!(messageContainer instanceof HTMLElement)) {
        return;
    }

    messageContainer.replaceChildren();

    const messageText = document.createElement('span');
    messageText.textContent = text;
    messageContainer.appendChild(messageText);
    messageContainer.style.visibility = 'visible';

    if (messageTimeoutId != null) {
        window.clearTimeout(messageTimeoutId);
    }

    messageTimeoutId = window.setTimeout(() => {
        messageContainer.style.visibility = 'hidden';
    }, MESSAGE_VISIBLE_MS);
}

function adjustFullScreen(): void {
    const fullScreenElement = document.querySelector('.container') as HTMLElement;

    if (fullScreenElement) {
        const menuBar = document.querySelector('.top-menu-bar') as HTMLElement | null;
        const menuBarHeight = menuBar?.offsetHeight ?? 0;
        fullScreenElement.style.height = `${Math.max(0, window.innerHeight - menuBarHeight)}px`;
    }
}

window.addEventListener('resize', adjustFullScreen);
window.addEventListener('orientationchange', adjustFullScreen);
adjustFullScreen();
